import { useSyncExternalStore } from 'react';

export const FACILITY_ID_KEY = 'facilityId';

const listeners = new Set<() => void>();

/**
 * Reactive access to the session's facility.
 *
 * `facilityId` has always lived in localStorage, which React cannot observe — consumers only saw a
 * new value when something else happened to re-render them. That was survivable while
 * `SplitFactoryProvider` was being handed a fresh config object on every render (it tore down and
 * re-created the whole SDK constantly, which re-rendered everything downstream), but that churn is
 * exactly what we removed. Reading through this store instead gives facility-scoped consumers — the
 * Split client key in `useFeatureToggle` above all — a real subscription.
 *
 * It matters because the two facility switchers don't behave the same way: the vertical layout
 * navigates on success (`layouts/dashboard/vertical-layout/top-nav.tsx`) and the horizontal one
 * does not (`layouts/dashboard/horizontal-layout/top-nav.tsx`), so there is no remount to lean on.
 */
export function getFacilityId(): string {
  return localStorage.getItem(FACILITY_ID_KEY) || '';
}

/**
 * Set the session's facility and notify subscribers. Prefer this over a bare
 * `localStorage.setItem('facilityId', …)` — a direct write is invisible to `useFacilityId`.
 */
export function setFacilityId(facilityId: string): void {
  localStorage.setItem(FACILITY_ID_KEY, facilityId ?? '');
  listeners.forEach((listener) => listener());
}

function subscribe(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);

  //  `storage` fires in other tabs only, so it complements rather than replaces the explicit
  //  notification in setFacilityId. A null key means the whole store was cleared (logout).
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === FACILITY_ID_KEY) onStoreChange();
  };
  window.addEventListener('storage', onStorage);

  return () => {
    listeners.delete(onStoreChange);
    window.removeEventListener('storage', onStorage);
  };
}

export function useFacilityId(): string {
  return useSyncExternalStore(subscribe, getFacilityId);
}
